import { isInstallation } from "./installation";
import { type ReactNode, useMemo } from "react";

import {
  type Identifier,
  useGetIdentity,
  useGetList,
  useTimeout,
  useTranslate,
} from "ra-core";

import { useIsMobile } from "@/hooks/use-mobile";

import type { Task } from "../types";

import { TaskListFilter } from "./TasksListFilter";

import {
  isDueLater,
  isDueThisWeek,
  isDueToday,
  isDueTomorrow,
  isOverdue,
} from "./tasksPredicate";

export type TaskListMode = "mine" | "created" | "all";

type ContactTask = Task & {
  linked_contact_id?: Identifier | null;
};

type TasksListByDueDateProps = {
  filterByContact?: Identifier;

  emptyPlaceholder?: ReactNode;

  pendingPlaceholder?: ReactNode;

  mode?: TaskListMode;

  showLater?: boolean;
};

export const TasksListByDueDate = ({
  filterByContact,
  emptyPlaceholder,
  pendingPlaceholder,
  mode = "mine",
  showLater = true,
}: TasksListByDueDateProps) => {
  const { identity } = useGetIdentity();

  const isMobile = useIsMobile();

  const translate = useTranslate();

  /*
   * В карточке контакта читаем
   * специальный view contact_tasks.
   *
   * В обычном разделе задач
   * продолжаем читать public.tasks.
   */
  const resource = filterByContact != null ? "contact_tasks" : "tasks";

  /*
   * Фильтр карточки контакта.
   */
  const contactFilter =
    filterByContact != null
      ? {
          linked_contact_id: filterByContact,
        }
      : null;

  /*
   * Фильтр обычного списка задач.
   */
  const regularFilter =
    mode === "created"
      ? {
          created_by_sales_id: identity?.id,

          "sales_id@neq": identity?.id,
        }
      : mode === "all"
        ? {}
        : {
            sales_id: identity?.id,
          };

  const filter = contactFilter ?? regularFilter;

  const {
    data: loadedTasks = [],
    isPending,
    error,
  } = useGetList<ContactTask>(
    resource,
    {
      pagination: {
        page: 1,
        perPage: 1000,
      },

      sort: {
        field: "due_date",
        order: "ASC",
      },

      filter,
    },
    {
      enabled: filterByContact != null ? true : !!identity,
    },
  );

  const { data: sharedTasks = [] } = useGetList<ContactTask>(
    "tasks",
    {
      pagination: { page: 1, perPage: 10000 },
      sort: { field: "due_date", order: "ASC" },
      filter: { team_task: true },
    },
    { enabled: !!identity && filterByContact == null },
  );
  if (error) {
    console.error(`Failed to load ${resource}:`, error);
  }

  /*
   * View contact_tasks в норме
   * и так возвращает одну строку
   * на пару контакт-задача.
   *
   * Но дополнительно удаляем
   * возможные дубли по task.id.
   */
  const tasks = useMemo(() => {
    const unique = new Map<string, ContactTask>();

    for (const task of [
      ...loadedTasks,
      ...sharedTasks.filter(
        (t) =>
          mode !== "created" ||
          String(t.created_by_sales_id) === String(identity?.id),
      ),
    ]) {
      if (!isInstallation(task)) unique.set(String(task.id), task);
    }

    return Array.from(unique.values());
  }, [loadedTasks, sharedTasks, mode, identity?.id]);

  /*
   * В карточке контакта
   * имя самого контакта рядом
   * с каждой задачей не нужно.
   */
  const showContact = filterByContact == null;

  const showAssignee =
    filterByContact == null && (mode === "created" || mode === "all");

  /*
   * Выполненные задачи оставляем
   * видимыми ещё 5 минут после
   * завершения.
   *
   * Здесь делаем проверку прямо,
   * чтобы не зависеть от старой
   * типизации isDone /
   * isRecentlyDone.
   */
  const ongoingTasks = useMemo(() => {
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;

    return tasks.filter((task) => {
      if (!task.done_date) {
        return true;
      }

      const doneTime = new Date(task.done_date).getTime();

      return Number.isFinite(doneTime) && doneTime >= fiveMinutesAgo;
    });
  }, [tasks]);

  const datedTasks = ongoingTasks;
  const overdueTasks = useMemo(
    () => datedTasks.filter((task) => isOverdue(task.due_date)),
    [datedTasks],
  );

  const dueTodayTasks = useMemo(
    () => datedTasks.filter((task) => isDueToday(task.due_date)),
    [datedTasks],
  );

  const dueTomorrowTasks = useMemo(
    () => datedTasks.filter((task) => isDueTomorrow(task.due_date)),
    [datedTasks],
  );

  const dueThisWeekTasks = useMemo(
    () => datedTasks.filter((task) => isDueThisWeek(task.due_date)),
    [datedTasks],
  );

  const dueLaterTasks = useMemo(
    () => datedTasks.filter((task) => isDueLater(task.due_date)),
    [datedTasks],
  );

  const oneSecondHasPassed = useTimeout(1000);

  if (isPending && oneSecondHasPassed) {
    return pendingPlaceholder ?? null;
  }

  if (isPending) {
    return null;
  }

  if (ongoingTasks.length === 0) {
    return emptyPlaceholder ?? null;
  }

  return (
    <div className="flex flex-col gap-4">
      {overdueTasks.length > 0 && (
        <TaskListFilter
          tasks={overdueTasks}
          title={translate("resources.tasks.filters.overdue")}
          showContact={showContact}
          showAssignee={showAssignee}
          isMobile={isMobile}
        />
      )}

      {dueTodayTasks.length > 0 && (
        <TaskListFilter
          tasks={dueTodayTasks}
          title={translate("resources.tasks.filters.today")}
          showContact={showContact}
          showAssignee={showAssignee}
          isMobile={isMobile}
        />
      )}

      {dueTomorrowTasks.length > 0 && (
        <TaskListFilter
          tasks={dueTomorrowTasks}
          title={translate("resources.tasks.filters.tomorrow")}
          showContact={showContact}
          showAssignee={showAssignee}
          isMobile={isMobile}
        />
      )}

      {dueThisWeekTasks.length > 0 && (
        <TaskListFilter
          tasks={dueThisWeekTasks}
          title={translate("resources.tasks.filters.this_week")}
          showContact={showContact}
          showAssignee={showAssignee}
          isMobile={isMobile}
        />
      )}

      {showLater && dueLaterTasks.length > 0 && (
        <TaskListFilter
          tasks={dueLaterTasks}
          title={translate("resources.tasks.filters.later")}
          showContact={showContact}
          showAssignee={showAssignee}
          isMobile={isMobile}
        />
      )}
    </div>
  );
};

/*
 * Оставляем также default export.
 *
 * Поэтому файл теперь работает
 * и с:
 *
 * import { TasksListByDueDate } ...
 *
 * и с:
 *
 * import TasksListByDueDate ...
 */
export default TasksListByDueDate;
